import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Award,
  Users,
  Leaf,
  Shield,
  ArrowRight,
  CheckCircle2 } from
'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const values = [
{
  icon: Award,
  title: 'Quality First',
  description: 'We use only premium commercial-grade parts and materials. No shortcuts, no compromises.'
},
{
  icon: Users,
  title: 'Customer Focus',
  description: 'Your satisfaction is our priority. We listen, adapt, and deliver beyond expectations.'
},
{
  icon: Leaf,
  title: 'Eco-Friendly',
  description: 'Water conservation and sustainable practices are at the heart of everything we do.'
},
{
  icon: Shield,
  title: 'Reliability',
  description: 'Licensed, insured, and committed to standing behind our work with comprehensive warranties.'
}];


const About = () => {
  return (
    <div className="pt-24 md:pt-28" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-mist-dark" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center">

            <span className="inline-block px-4 py-2 bg-landscape-green/20 text-landscape-green font-body text-sm font-bold uppercase tracking-widest rounded-full mb-6">
              About Us
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-mist-white mb-6">
              Your Local Irrigation <br />
              <span className="text-mist-light">Experts</span>
            </h1>
            <p className="font-body text-lg md:text-xl text-mist-light/80 max-w-2xl mx-auto">
              For over 15 years, Mist Irrigation has been helping homeowners and businesses 
              create beautiful, sustainable landscapes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32 bg-mist-white" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <span className="inline-block px-4 py-2 bg-mist-light text-mist-blue font-body text-sm font-bold uppercase tracking-widest rounded-full mb-4">
                Our Story
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-mist-dark mb-6">Locally Owned. Irrigation Done Right.

              </h2>
              <p className="font-body text-lg text-mist-blue leading-relaxed mb-6">Mist Irrigation is built on more than 30 years of hands-on experience across commercial properties, residential systems, and golf course installations. Our founder, Jerimi Brink, has designed and installed high-performance irrigation systems where precision, efficiency and long-term reliability matter most.




              </p>
              <p className="font-body text-lg text-mist-blue leading-relaxed mb-6">We believe irrigation should be done proactively, not reactively. From new system installations to structured Seasonal Care Programs, our focus is on long-term performance, water efficiency, and dependable service.                                                                                                                                  As as a locally owned and operated company, we take pride in delivering professional workmanship and results you can count on.




              </p>
              <div className="grid grid-cols-3 gap-8 mt-8">
                <div className="text-center">
                  <div className="font-heading text-4xl font-bold text-landscape-green">30+</div>
                  <div className="font-body text-sm text-mist-blue">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="font-heading text-4xl font-bold text-landscape-green">2500+</div>
                  <div className="font-body text-sm text-mist-blue">Projects Completed</div>
                </div>
                <div className="text-center">
                  <div className="font-heading text-4xl font-bold text-landscape-green">100%</div>
                  <div className="font-body text-sm text-mist-blue">Customer Satisfaction</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>

              <img
                src="https://customer-assets.emergentagent.com/job_yard-care-expert/artifacts/njfzkkgj_sprinklerimg1.jpg"
                alt="Our team at work"
                className="rounded-3xl shadow-2xl" />

            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-mist-light" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-mist-blue/20 text-mist-blue font-body text-sm font-bold uppercase tracking-widest rounded-full mb-4">
              Our Values
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-mist-dark mb-6">
              What Drives Us
            </h2>
            <p className="font-body text-lg text-mist-blue max-w-2xl mx-auto">
              Our core values guide every decision we make and every project we undertake.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}>

                <Card className="bg-white border-0 shadow-lg rounded-2xl h-full card-hover" data-testid={`value-card-${index}`}>
                  <CardContent className="p-8 text-center">
                    <div className="w-14 h-14 bg-mist-light rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <value.icon className="w-7 h-7 text-mist-blue" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-mist-dark mb-3">
                      {value.title}
                    </h3>
                    <p className="font-body text-mist-blue">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 md:py-32 bg-mist-dark" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <span className="inline-block px-4 py-2 bg-landscape-green/20 text-landscape-green font-body text-sm font-bold uppercase tracking-widest rounded-full mb-4">
                Credentials
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-mist-white mb-6">
                Licensed, Insured & Certified
              </h2>
              <p className="font-body text-lg text-mist-light/80 leading-relaxed mb-8">
                We maintain all necessary licenses and certifications to ensure the highest 
                quality work and complete peace of mind for our customers.
              </p>
              <ul className="space-y-4">
                {[
                'State  Irrigation Standards',
                'EPA WaterSense Standards',
                'Backflow Prevention ',
                'Landscape Lighting Certified',
                'Fully Bonded and Insured'].
                map((item, index) =>
                <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-landscape-green flex-shrink-0" />
                    <span className="font-body text-mist-light">{item}</span>
                  </li>
                )}
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-6">

              {[
              { number: '30+', label: 'Years in Business' },
              { number: '20+', label: 'Team Members' },
              { number: '100%', label: 'Satisfaction Guarantee' },
              { number: '24/7', label: 'Emergency Service' }].
              map((stat, index) =>
              <Card key={index} className="bg-mist-blue/20 border-0 rounded-2xl" data-testid={`stat-card-${index}`}>
                  <CardContent className="p-8 text-center">
                    <div className="font-heading text-4xl font-bold text-landscape-green mb-2">
                      {stat.number}
                    </div>
                    <div className="font-body text-mist-light">
                      {stat.label}
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-landscape-green" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
              Ready to Work With Us?
            </h2>
            <p className="font-body text-lg text-white/90 max-w-2xl mx-auto mb-8">
              Let's discuss your project and create something beautiful together.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-white text-landscape-green hover:bg-mist-white rounded-full px-8 py-6 text-lg font-medium"
                data-testid="about-cta-btn">

                Get in Touch
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>);

};

export default About;